import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Link,
  Snackbar,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  DeleteOutline as DeleteIcon,
  Link as LinkIcon,
  Movie as MovieIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../../services/api';

// Product video for the Mobla home page ("See it in action" section), one link
// per language. The admin backend stores them:
//   GET /site-video/home · PUT /site-video/home/:lang/link ({ url }) · DELETE /site-video/home/:lang
// English is the fallback: Finnish visitors see it when there is no Finnish video.

type Lang = 'en' | 'fi';
type Provider = 'youtube' | 'vimeo' | 'file';

interface SiteVideo {
  lang: Lang;
  url: string;
  provider: Provider;
  embed_id?: string;
  embed_hash?: string;
  updated_at?: string;
}

const LANGS: { lang: Lang; label: string; note: string }[] = [
  { lang: 'en', label: 'English', note: 'Shown to English visitors, and to Finnish visitors when there is no Finnish video.' },
  { lang: 'fi', label: 'Finnish', note: 'Shown to Finnish visitors.' },
];

const PROVIDER_LABEL: Record<Provider, string> = {
  youtube: 'YouTube link',
  vimeo: 'Vimeo link',
  file: 'Video file link',
};

const EMPTY = { en: null, fi: null } as Record<Lang, SiteVideo | null>;

// Same players the home page uses.
const embedUrl = (video: SiteVideo) => {
  if (video.provider === 'youtube' && video.embed_id) {
    return `https://www.youtube-nocookie.com/embed/${video.embed_id}?rel=0&modestbranding=1`;
  }
  if (video.provider === 'vimeo' && video.embed_id) {
    return `https://player.vimeo.com/video/${video.embed_id}?${video.embed_hash ? `h=${video.embed_hash}&` : ''}dnt=1`;
  }
  return null;
};

const formatDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '';

const errorMessage = (error: any, fallback: string) => error?.response?.data?.message || fallback;

const Preview: React.FC<{ video: SiteVideo; label: string }> = ({ video, label }) => {
  const style: React.CSSProperties = { width: '100%', height: '100%', border: 0, display: 'block' };
  const embed = embedUrl(video);
  if (embed) {
    return <iframe key={embed} src={embed} title={`${label} video`} style={style} allow="fullscreen; picture-in-picture; encrypted-media" allowFullScreen />;
  }
  return <video key={video.url} src={video.url} controls preload="metadata" style={{ ...style, objectFit: 'contain' }} />;
};

const HomeVideo: React.FC = () => {
  const theme = useTheme();
  const [videos, setVideos] = useState<Record<Lang, SiteVideo | null>>(EMPTY);
  const [linkInput, setLinkInput] = useState<Record<Lang, string>>({ en: '', fi: '' });
  const [saving, setSaving] = useState<Record<Lang, boolean>>({ en: false, fi: false });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [confirmRemove, setConfirmRemove] = useState<Lang | null>(null);
  const [removing, setRemoving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const langName = (lang: Lang) => LANGS.find(l => l.lang === lang)?.label || lang;
  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnackbar({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await api.get('/site-video/home');
      setVideos({ en: res.data?.data?.en || null, fi: res.data?.data?.fi || null });
    } catch (error: any) {
      setLoadError(errorMessage(error, 'Could not load the current videos.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveLink = async (lang: Lang) => {
    const url = linkInput[lang].trim();
    if (!url) {
      showSnackbar('Paste a video link first.', 'error');
      return;
    }
    setSaving(s => ({ ...s, [lang]: true }));
    try {
      const res = await api.put(`/site-video/home/${lang}/link`, { url });
      setVideos(v => ({ ...v, [lang]: res.data?.data || null }));
      setLinkInput(l => ({ ...l, [lang]: '' }));
      showSnackbar(`${langName(lang)} video saved. It is live on the home page now.`);
    } catch (error: any) {
      showSnackbar(errorMessage(error, 'Could not save the link. Please try again.'), 'error');
    } finally {
      setSaving(s => ({ ...s, [lang]: false }));
    }
  };

  const remove = async () => {
    if (!confirmRemove) return;
    const lang = confirmRemove;
    setRemoving(true);
    try {
      await api.delete(`/site-video/home/${lang}`);
      setVideos(v => ({ ...v, [lang]: null }));
      showSnackbar(`${langName(lang)} video removed.`);
    } catch (error: any) {
      showSnackbar(errorMessage(error, 'Could not remove the video.'), 'error');
    } finally {
      setRemoving(false);
      setConfirmRemove(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
            Home page video
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 760 }}>
            The product video in the "See it in action" section of the Mobla home page, right below the hero. For each
            language, paste a link (YouTube, Vimeo or a direct video file). The section is hidden while neither language
            has a video.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {loadError && (
        <Alert severity="error" sx={{ mb: 3 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {loadError}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {LANGS.map(({ lang, label, note }) => {
            const video = videos[lang];
            const busy = saving[lang];
            return (
              <Grid item xs={12} md={6} key={lang}>
                <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                      <Typography variant="h6" fontWeight={600}>{label}</Typography>
                      <Chip
                        size="small"
                        label={video ? `Live · ${PROVIDER_LABEL[video.provider] || 'Link'}` : 'No video'}
                        color={video ? 'success' : 'default'}
                        variant={video ? 'filled' : 'outlined'}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {note}
                    </Typography>

                    <Box
                      sx={{
                        position: 'relative',
                        aspectRatio: '16 / 9',
                        borderRadius: 2,
                        overflow: 'hidden',
                        bgcolor: video ? '#000' : alpha(theme.palette.primary.main, 0.05),
                        border: `1px dashed ${alpha(theme.palette.primary.main, video ? 0 : 0.35)}`,
                        display: 'grid',
                        placeItems: 'center',
                      }}
                    >
                      {video ? (
                        <Preview video={video} label={label} />
                      ) : (
                        <Stack alignItems="center" spacing={1} sx={{ color: 'text.secondary', px: 2, textAlign: 'center' }}>
                          <MovieIcon sx={{ fontSize: 40, opacity: 0.6 }} />
                          <Typography variant="body2">No {label} video yet</Typography>
                        </Stack>
                      )}
                    </Box>

                    {video && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, wordBreak: 'break-all' }}>
                        <Link href={video.url} target="_blank" rel="noopener noreferrer">{video.url}</Link>
                        {video.updated_at ? ` · saved ${formatDate(video.updated_at)}` : ''}
                      </Typography>
                    )}

                    <Box
                      component="form"
                      sx={{ mt: 2.5 }}
                      onSubmit={(e: React.FormEvent) => {
                        e.preventDefault();
                        saveLink(lang);
                      }}
                    >
                      <TextField
                        id={`video-link-${lang}`}
                        fullWidth
                        size="small"
                        label={video ? 'New video link' : 'Video link'}
                        placeholder="https://www.youtube.com/watch?v=…"
                        value={linkInput[lang]}
                        onChange={e => setLinkInput(l => ({ ...l, [lang]: e.target.value }))}
                        disabled={busy}
                        helperText="YouTube or Vimeo link, or a direct https link to an .mp4 or .webm file."
                      />
                      <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                        <Button type="submit" variant="contained" startIcon={<LinkIcon />} disabled={busy || !linkInput[lang].trim()}>
                          {busy ? 'Saving…' : video ? 'Replace video' : 'Save link'}
                        </Button>
                        {video && (
                          <Button variant="outlined" color="error" startIcon={<DeleteIcon />} disabled={busy} onClick={() => setConfirmRemove(lang)}>
                            Remove
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
        Tip: unlisted YouTube or Vimeo videos work too, so the video doesn't have to be public on your channel.
      </Typography>

      <Dialog open={!!confirmRemove} onClose={() => !removing && setConfirmRemove(null)}>
        <DialogTitle>Remove the {confirmRemove ? langName(confirmRemove) : ''} video?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmRemove === 'fi'
              ? 'Finnish visitors will see the English video instead, if there is one.'
              : 'English visitors, and Finnish visitors without a Finnish video, will no longer see a video. If no language has a video, the section disappears from the home page.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(null)} disabled={removing}>Cancel</Button>
          <Button color="error" variant="contained" onClick={remove} disabled={removing}>
            {removing ? 'Removing…' : 'Remove video'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HomeVideo;
